import { Payments, SplitPaymentsGroups } from '@prisma/client';
import { AxiosError } from 'axios';
import API_URL from '../apiconfig';
import {
  CheckoutCustomer,
  CheckoutData,
  CreateSplitPaymentPayload,
  CreateSplitPaymentResponse,
  CreateSplitPaymentsGroupPayload,
  FullPayment,
  InitiatePaymentPayload,
  InitiatePaymentResponse,
  PaymentsGroup,
  PaymentsGroupDetail,
  PaymentsGroupResponse,
  RefundPaymentPayload,
  RefundPaymentResponse,
  SortableKeys,
  SplitPaymentsGroupWithCustomer,
  SplitPaymentsGroupWithPayments,
} from '../types/payments';
import axios from './api';
import { getApiErrorMessage } from './apiError';
import { updateCustomer } from './user';
import { updateCustomerGuest } from './customers';
import { getCustomerUpdateError } from './appointments';
import { SortValues } from '../types/ui';
import client from '../utils/react-query/client';
import { Medium } from '../types/global';

export async function refundPayment(
  paymentId: string,
  payload: RefundPaymentPayload,
) {
  const paymentResult = await axios.patch(
    `${API_URL}/v1/payments/${paymentId}/refund`,
    payload,
  );

  return paymentResult.data.result as RefundPaymentResponse;
}

export async function sendPaymentReceipt(
  paymentId: string,
  mediums: Medium[],
  email?: string | null,
  phone?: string | null,
) {
  try {
    const paymentResult = await axios.post(
      `${API_URL}/v1/payments/${paymentId}/receipt`,
      {
        mediums,
        email,
        phone,
      },
    );

    return paymentResult.data.result as Payments;
  } catch (error) {
    if ((error as AxiosError).response?.status === 429) {
      throw new Error('Please wait before sending another receipt', {
        cause: error,
      });
    }
    throw new Error('Failed to send payment receipt', { cause: error });
  }
}

export async function updateCustomerBeforePayment(customer: CheckoutCustomer) {
  let customerId: string | undefined = customer?.customerId;

  if (customer?.customerGuestId && customer?.customerId) {
    await updateCustomerGuest({
      customerId: customer.customerId,
      guestId: customer.customerGuestId,
      data: {
        firstName: customer.firstName,
        lastName: customer.lastName ?? undefined,
        email: customer.email ?? undefined,
        dob: customer.dob ?? undefined,
      },
    }).catch(err => {
      throw new Error(getCustomerUpdateError(err));
    });
  } else if (customer?.customerId) {
    await updateCustomer(
      {
        firstName: customer.firstName,
        lastName: customer.lastName ?? undefined,
        phoneNumber: customer.phoneNumber,
        email: customer.email ?? undefined,
        dob: customer.dob ?? undefined,
      },
      customer.customerId,
    ).catch(err => {
      throw new Error(getCustomerUpdateError(err));
    });
  } else if (customer) {
    const phoneNumber =
      customer.phoneNumber?.charAt(0) === '+'
        ? customer.phoneNumber
        : `+${customer.phoneNumber}`;

    const createCustomerPayload: any = {
      firstName: customer.firstName,
      lastName: customer.lastName,
      phoneNumber,
    };

    if (customer.email) {
      createCustomerPayload.email = customer.email;
    }

    if (customer.dob) {
      createCustomerPayload.dob = customer.dob;
    }

    const createCustomerResponse = await axios
      .post(`${API_URL}/v1/customers`, createCustomerPayload)
      .catch(error => {
        throw new Error(getApiErrorMessage(error, 'Cannot create customer'), {
          cause: error,
        });
      });

    customerId = createCustomerResponse.data.result.customerId;
  }

  return customerId;
}

export async function initiatePayment(
  payload: InitiatePaymentPayload,
  customer?: CheckoutCustomer,
  { guest }: { guest?: boolean } = {},
) {
  try {
    const customerId =
      !guest && customer
        ? await updateCustomerBeforePayment(customer)
        : undefined;
    const paymentResult = await axios.post(`${API_URL}/v1/payments`, {
      ...payload,
      customerId: guest ? null : customerId || payload.customerId,
    });

    return paymentResult.data.result as InitiatePaymentResponse;
  } catch (error) {
    throw new Error('Failed to initiate payment', { cause: error });
  }
}

export async function getCheckoutData(
  payload: InitiatePaymentPayload,
  { signal }: { signal?: AbortSignal } = {},
) {
  try {
    const paymentResult = await axios.post(
      `${API_URL}/v1/payments/calculate`,
      {
        ...payload,
        isEstimateOnly: true,
      },
      { signal },
    );

    return paymentResult.data.result as CheckoutData;
  } catch (error) {
    throw new Error('Failed to get checkout data', { cause: error });
  }
}

export async function cancelCheckout({
  appointmentId,
  orderId,
  terminalReaderId,
}: {
  appointmentId?: string;
  orderId?: string;
  terminalReaderId?: string;
}) {
  try {
    const paymentResult = await axios.patch(
      `${API_URL}/v1/payments/cancel-checkout`,
      {
        appointmentId,
        orderId,
        terminalReaderId,
      },
    );

    return paymentResult.data.result as {
      appointmentId: string | undefined;
      orderId: string | undefined;
    };
  } catch (error) {
    throw new Error('Failed to cancel checkout', { cause: error });
  }
}

export async function getGroupedPayments({
  businessId,
  page = 1,
  sort,
}: {
  businessId: string;
  page?: number;
  sort?: SortValues<SortableKeys>;
}) {
  try {
    const paymentResult = await axios.get(`${API_URL}/v1/payments`, {
      params: {
        businessId,
        page,
        sortOrder: sort?.order,
        sortOrderBy: sort?.orderBy,
      },
    });

    return paymentResult.data.result as PaymentsGroupResponse;
  } catch (error) {
    throw new Error('Failed to get grouped payments', { cause: error });
  }
}

export async function getPaymentsGroup({
  groupPaymentsId,
}: {
  groupPaymentsId: string;
}) {
  try {
    const paymentResult = await axios.get(
      `${API_URL}/v1/payments/group/${groupPaymentsId}`,
    );

    return paymentResult.data.result as PaymentsGroup;
  } catch (error) {
    throw new Error('Failed to get payments group', { cause: error });
  }
}

export async function getPayment({
  paymentId,
  signal,
}: {
  paymentId: string;
  signal?: AbortSignal;
}) {
  try {
    const paymentResult = await axios.get(
      `${API_URL}/v1/payments/${paymentId}`,
      { signal },
    );

    return paymentResult.data.result as FullPayment;
  } catch (error) {
    throw new Error('Failed to get payment', { cause: error });
  }
}

export async function getPaymentPaymentsGroup({
  paymentId,
}: {
  paymentId: string;
}) {
  try {
    const paymentResult = await axios.get(
      `${API_URL}/v1/payments/${paymentId}/group`,
    );

    return paymentResult.data.result as PaymentsGroupDetail;
  } catch (error) {
    throw new Error('Failed to get payment payments group', { cause: error });
  }
}

export async function voidPayment({
  paymentId,
  appointmentPaymentBehavior,
}: {
  paymentId: string;
  appointmentPaymentBehavior?: 'open' | 'free';
}) {
  try {
    const paymentResult = await axios.delete(
      `${API_URL}/v1/payments/${paymentId}`,
      {
        params: {
          appointmentPaymentBehavior,
        },
      },
    );

    return paymentResult.data.result as Payments;
  } catch (error) {
    throw new Error('Failed to void payment', { cause: error });
  }
}

export async function getSplitPaymentsGroup({
  splitPaymentsGroupId,
}: {
  splitPaymentsGroupId: string;
}) {
  try {
    const paymentResult = await axios.get(
      `${API_URL}/v1/payments/split/${splitPaymentsGroupId}`,
    );

    return paymentResult.data.result as SplitPaymentsGroupWithPayments;
  } catch (error) {
    throw new Error('Failed to get split payments group', { cause: error });
  }
}
export async function createSplitPaymentsGroup(
  payload: CreateSplitPaymentsGroupPayload,
) {
  try {
    const paymentResult = await axios.post(
      `${API_URL}/v1/payments/split`,
      payload,
    );

    return paymentResult.data.result as SplitPaymentsGroupWithCustomer;
  } catch (error) {
    throw new Error('Failed to create split payments group', { cause: error });
  }
}

export async function createSplitPayment(
  splitPaymentsGroupId: string,
  payload: CreateSplitPaymentPayload,
) {
  try {
    const paymentResult = await axios.post(
      `${API_URL}/v1/payments/split/${splitPaymentsGroupId}/payment`,
      payload,
    );

    return paymentResult.data.result as CreateSplitPaymentResponse;
  } catch (error) {
    throw new Error('Failed to create split payment', { cause: error });
  }
}

export async function resetSplitPaymentsGroup(splitPaymentsGroupId: string) {
  try {
    const paymentResult = await axios.patch(
      `${API_URL}/v1/payments/split/${splitPaymentsGroupId}/reset`,
    );

    return paymentResult.data.result as SplitPaymentsGroups;
  } catch (error) {
    throw new Error('Failed to reset split payments group', { cause: error });
  }
}

export async function finalizeSplitPayments(splitPaymentsGroupId: string) {
  try {
    const paymentResult = await axios.post(
      `${API_URL}/v1/payments/split/${splitPaymentsGroupId}/charge`,
    );

    return paymentResult.data.result as SplitPaymentsGroupWithPayments;
  } catch (error) {
    throw new Error('Failed to finalize split payments', { cause: error });
  }
}

export async function processTerminalPayment(paymentId: string) {
  try {
    const paymentResult = await axios.post(
      `${API_URL}/v1/payments/${paymentId}/terminal`,
    );

    return paymentResult.data.result as Payments;
  } catch (error) {
    throw new Error('Failed to process terminal payment', { cause: error });
  }
}

export async function invalidateGroupedPayments() {
  await client.invalidateQueries({
    queryKey: ['grouped-payments'],
    exact: false,
  });
}

export async function invalidatePaymentPaymentsGroup(paymentId: string) {
  await client.invalidateQueries({
    queryKey: ['payment-payments-group', paymentId],
    exact: false,
  });
}

export async function invalidatePaymentPaymentsGroups() {
  await client.invalidateQueries({
    queryKey: ['payment-payments-group'],
    exact: false,
  });
}

export async function invalidateCreateSplitPaymentsGroup() {
  await client.invalidateQueries({
    queryKey: ['create-split-payments-group'],
    exact: false,
  });
}
